import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { BigMetricCard } from "../components/MetricCard";
import TrendChart from "../components/TrendChart";
import OutletComparison from "../components/OutletComparison";
import TopProducts from "../components/TopProducts";
import EmergingProducts from "../components/EmergingProducts";
import NewArrivals from "../components/NewArrivals";
import CategoriesRollup from "../components/CategoriesRollup";
import AccessoriesPanel from "../components/AccessoriesPanel";
import EyewearPanel from "../components/EyewearPanel";
import InsightsPanel from "../components/InsightsPanel";
import CompetitorPanel from "../components/CompetitorPanel";
import DateRangePicker from "../components/DateRangePicker";
import ComparisonPicker from "../components/ComparisonPicker";
import StoreMultiSelect from "../components/StoreMultiSelect";
import ViewsMenu from "../components/ViewsMenu";
import FilterBar from "../components/FilterBar";
import StoreSwiper from "../components/StoreSwiper";
import TodayBox from "../components/TodayBox";
import { useSwipe } from "../lib/useMobile";
import SellThroughAlerts from "../components/SellThroughAlerts";
import { useAuth, homeStores } from "../context/AuthContext";
import { storeName } from "../lib/labels";

function money(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

const TREND_BUCKETS = 5;

/** ISO date for the start of the bucket that sits TREND_BUCKETS-1 buckets before `endIso`'s bucket. */
function trendWindowStart(endIso, granularity) {
  const [y, m, d] = endIso.split("-").map(Number);
  const back = TREND_BUCKETS - 1;
  let start;
  if (granularity === "day") {
    start = new Date(Date.UTC(y, m - 1, d - back));
  } else if (granularity === "week") {
    const end = new Date(Date.UTC(y, m - 1, d));
    const dow = (end.getUTCDay() + 6) % 7; // Monday = 0 (ISO weeks)
    start = new Date(Date.UTC(y, m - 1, d - dow - back * 7));
  } else if (granularity === "month") {
    start = new Date(Date.UTC(y, m - 1 - back, 1));
  } else if (granularity === "quarter") {
    const q0 = Math.floor((m - 1) / 3) * 3;
    start = new Date(Date.UTC(y, q0 - back * 3, 1));
  } else {
    start = new Date(Date.UTC(y - back, 0, 1));
  }
  return start.toISOString().slice(0, 10);
}

export default function Dashboard() {
  const { user } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [outletIds, setOutletIds] = useState(() => homeStores(user)); // [] = all stores
  const [range, setRange] = useState(null);
  const [pickerValue, setPickerValue] = useState({ state: { tab: "period", unit: "Week", mode: "previous_n", n: 3 }, stateKey: 0 });
  const [compareMode, setCompareMode] = useState("same_period");
  const [headlineMulti, setHeadlineMulti] = useState(null);
  const [outletComp, setOutletComp] = useState([]);
  const [trend, setTrend] = useState([]);
  const [sparkTrend, setSparkTrend] = useState([]);
  const [top, setTop] = useState([]);
  const [emerging, setEmerging] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  const outletParam = outletIds.length ? { outlet_ids: outletIds.join(",") } : {};

  async function loadOutlets() {
    try {
      const { data } = await api.get("/outlets");
      const order = ["Black Market 347", "Black Market 256", "Public Butter Kensington", "Dirty Mike's"];
      data.sort((a, b) => {
        const ai = order.indexOf(a.name);
        const bi = order.indexOf(b.name);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      setOutlets(data);
    } catch (e) {}
  }

  async function loadAll() {
    if (!range) return;
    const reqId = ++reqIdRef.current;
    // Drop the previous range's data so the Store-vs-store bars and totals never
    // linger on stale months (e.g. showing June-first while a Jan-start range
    // loads). A brief empty state is honest; fresh data lands in <1s.
    setHeadlineMulti(null);
    setTrend([]);
    setOutletComp([]);
    setLoading(true);
    const guard = (setter) => (r) => {
      if (reqId === reqIdRef.current) setter(r.data);
    };
    const dateParams = { start_date: range.start, end_date: range.end };
    // Pick granularity: use the picker's unit for period modes; for YTD modes and
    // custom date ranges, auto-pick based on span so we always get enough data points.
    let granularity = (range?.state?.unit || "Month").toLowerCase();
    const mode = range?.state?.mode;
    if (mode === "ytd_calendar" || mode === "ytd_fiscal" || range?.state?.tab === "date") {
      const spanDays = (new Date(range.end) - new Date(range.start)) / 86400000;
      if (spanDays <= 60) granularity = "week";
      else if (spanDays <= 800) granularity = "month";
      else if (spanDays <= 3000) granularity = "quarter";
      else granularity = "year";
    }

    if (outletIds.length) {
      setOutletComp([]);
    } else {
      api.get("/metrics/outlet-comparison", { params: dateParams }).then(guard(setOutletComp)).catch(() => {});
    }
    const headlineP = api.get("/metrics/headline-multi", { params: { ...dateParams, ...outletParam } }).then(guard(setHeadlineMulti)).catch(() => {});
    // 4 Store Trend always shows the current bucket-to-date plus the 4 before it,
    // anchored to today (Toronto) whatever the headline range is.
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Toronto", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    const trendParams = { start_date: trendWindowStart(today, granularity), end_date: today };
    // Ghosts: `_prior` = previous same-length window; `_yoy` = 52 weeks back so
    // weekdays line up. The chart's own toggle picks which to draw.
    const trendP = api.get("/metrics/trend-with-weather", { params: { ...trendParams, granularity, include_yoy: true, yoy_mode: "same_period" } }).then(guard(setTrend)).catch(() => {});
    // 12-week weekly sparkline for the store-vs-store table (independent of the
    // main date filter so it always shows recent momentum)
    api.get("/metrics/trend", { params: { days: 84, granularity: "week" } }).then(guard(setSparkTrend)).catch(() => {});
    api.get("/products/top", { params: { ...dateParams, ...outletParam, limit: 12 } }).then(guard(setTop)).catch(() => {});
    api.get("/products/emerging", { params: outletParam }).then(guard(setEmerging)).catch(() => {});
    // Lift the "updating" state once the headline + trend (which drive the
    // metric cards and the Store-vs-store bars) have both landed.
    Promise.all([headlineP, trendP]).finally(() => {
      if (reqId === reqIdRef.current) setLoading(false);
    });
  }

  useEffect(() => { loadOutlets(); }, []);
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [range, outletIds.join(","), compareMode]);

  const cur = headlineMulti?.current;
  const comps = headlineMulti?.comparisons || [];
  const byOutlet = headlineMulti?.by_outlet || [];
  // Effective granularity for the trend chart title/formatter
  let granularityLabel = (range?.state?.unit || "Month");
  if (range) {
    const mode = range.state?.mode;
    if (mode === "ytd_calendar" || mode === "ytd_fiscal" || range.state?.tab === "date") {
      const spanDays = (new Date(range.end) - new Date(range.start)) / 86400000;
      if (spanDays <= 60) granularityLabel = "Week";
      else if (spanDays <= 800) granularityLabel = "Month";
      else if (spanDays <= 3000) granularityLabel = "Quarter";
      else granularityLabel = "Year";
    }
  }
  const scoped = user?.role === "staff" && (user?.outlet_ids?.length || 0) > 0;
  const swipe = useSwipe((dir) => {
    const stops = [null, ...outlets.map((o) => o.id)];
    if (stops.length <= 2) return;
    const cur = outletIds.length === 1 ? Math.max(0, stops.indexOf(outletIds[0])) : 0;
    const next = (cur + (dir === "left" ? 1 : -1) + stops.length) % stops.length;
    setOutletIds(stops[next] ? [stops[next]] : []);
  });
  const storeScopeLabel = outletIds.length === 0
    ? "All 4 stores combined"
    : outletIds.length === 1
      ? outlets.find((o) => o.id === outletIds[0])?.name
      : `${outletIds.length} stores combined`;

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Phone: today's sales, fresh from Lightspeed */}
      <TodayBox />

      {/* Filter bar */}
      <section className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="bm-eyebrow text-neutral-500">Toronto · Trends Desk</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Dashboard</h1>
        </div>
        <FilterBar summary={`${storeScopeLabel || "All stores"} · ${range?.label || ""}`} testId="filter-toolbar">
          <StoreMultiSelect outlets={outlets} value={outletIds} onChange={setOutletIds} />
          <DateRangePicker value={pickerValue} onChange={setRange} />
          <ComparisonPicker
            primaryStart={range?.start}
            primaryEnd={range?.end}
            mode={compareMode}
            onChange={({ mode }) => setCompareMode(mode)}
          />
          <ViewsMenu
            currentState={{ date: range?.state, outletIds, compareMode }}
            currentLabel={range?.label}
            outletCount={outletIds.length}
            onLoad={(v) => {
              const s = v.state || {};
              setOutletIds(s.outletIds || []);
              if (s.compareMode) setCompareMode(s.compareMode);
              if (s.date) setPickerValue({ state: s.date, stateKey: Date.now() });
            }}
          />
        </FilterBar>
      </section>

      <div className="bm-hairline" />

      {/* Scope banner */}
      <div className="flex items-baseline justify-between flex-wrap gap-2 bm-eyebrow text-neutral-600">
        <span className="flex items-center gap-3">
          {storeScopeLabel} · {range?.label || "—"}
          {loading && (
            <span data-testid="dashboard-updating" className="inline-flex items-center gap-1.5 text-[#c72c2c]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#c72c2c] animate-pulse" />
              Updating…
            </span>
          )}
        </span>
        <span className="text-neutral-400">All figures net of tax · every table uses the date picker's range unless it says "own window"</span>
      </div>

      {scoped && outletIds.length > 0 && (
        <div className="bm-eyebrow px-4 py-2 border border-[#0a0a0a]/40 text-neutral-600 bg-[#ede8dc]" data-testid="staff-scope-note">
          Opened on your home store{user.outlet_ids.length > 1 ? "s" : ""} — use the store picker in Filters to compare with the other locations.
        </div>
      )}

      {/* Phone: flip between stores by tapping or swiping the metrics */}
      <StoreSwiper outlets={outlets} outletIds={outletIds} onChange={setOutletIds} />

      {/* Metrics row — 4 boxes, prominent, each with 3 comparison rows + expandable per-store breakdown */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="metrics-row" {...swipe}>
        <BigMetricCard
          testId="metric-revenue"
          eyebrow="Revenue"
          value={money(cur?.revenue)}
          subValue={`${(cur?.units || 0).toLocaleString()} units`}
          comparisons={comps}
          byOutlet={byOutlet}
          deltaKey="growth_pct_revenue"
          valueKey="revenue"
          compareMode={compareMode}
          accent
        />
        <BigMetricCard
          testId="metric-transactions"
          eyebrow="Transactions"
          value={(cur?.transactions || 0).toLocaleString()}
          subValue={`${(cur?.units || 0).toLocaleString()} units moved`}
          comparisons={comps}
          byOutlet={byOutlet}
          deltaKey="growth_pct_transactions"
          valueKey="transactions"
          compareMode={compareMode}
        />
        <BigMetricCard
          testId="metric-aov"
          eyebrow="Avg. order"
          value={money(cur?.aov)}
          subValue={`per transaction`}
          comparisons={comps}
          byOutlet={byOutlet}
          deltaKey="growth_pct_aov"
          valueKey="aov"
          compareMode={compareMode}
        />
        <BigMetricCard
          testId="metric-pcs-per-order"
          eyebrow="Avg. pcs / order"
          value={cur?.pcs_per_order != null ? cur.pcs_per_order.toFixed(2) : "—"}
          subValue={`items per basket`}
          comparisons={comps}
          byOutlet={byOutlet}
          deltaKey="growth_pct_pcs_per_order"
          valueKey="pcs_per_order"
          compareMode={compareMode}
        />
      </section>

      {/* AI Insights — The Editor's Note, above the trend */}
      <InsightsPanel range={range} outletIds={outletIds} />

      {/* Trend — full width */}
      <section>
        <TrendChart data={trend} granularity={granularityLabel} loading={loading} />
      </section>

      {/* Outlet comparison — own row */}
      {!outletIds.length && (
        <section>
          <OutletComparison data={byOutlet} trend={trend} sparkTrend={sparkTrend} rangeLabel={range?.label} compareMode={compareMode} loading={loading} />
        </section>
      )}

      {/* Sell-through alerts — ±25% week-over-week movers */}
      <SellThroughAlerts outletIds={outletIds} />

      {/* Products */}
      <section className="grid lg:grid-cols-2 gap-4 [&>*]:min-w-0">
        <TopProducts range={range} />
        <EmergingProducts />
      </section>

      {/* Categories — top-level Lightspeed hierarchy, expandable */}
      <section>
        <CategoriesRollup range={range} outletIds={outletIds} compareMode={compareMode} />
      </section>

      {/* New Product Performance — SKUs with zero sales in the prior period */}
      <section>
        <NewArrivals />
      </section>

      {/* Accessories — jewelry, pins, belts, hats, sunglasses */}
      <section>
        <AccessoriesPanel range={range} outletIds={outletIds} compareMode={compareMode} />
      </section>

      {/* Eyewear & Sunglasses — SKU-level deep dive with YoY */}
      <section>
        <EyewearPanel range={range} compareMode={compareMode} />
      </section>

      {/* Regional competitor activity */}
      <CompetitorPanel />

      {/* Weekly brief */}
    </div>
  );
}
